import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Wallet,
  Receipt as ReceiptIcon,
  Users,
  Landmark,
  Beaker,
  RotateCcw,
} from "lucide-react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  CartesianGrid,
} from "recharts";
import { useStickyState } from "@/hooks/useStickyState";

const fmt = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("de-CH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const RANGES = [
  { value: 30, label: "30 T" },
  { value: 60, label: "60 T" },
  { value: 90, label: "90 T" },
];

export function CashflowForecastWidget({ companyId, days: defaultDays = 90 }) {
  const [days, setDays] = useStickyState(
    "cashflow-forecast-days",
    defaultDays,
    companyId
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter100b: Was-wäre-wenn Szenario
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [invoiceDelay, setInvoiceDelay] = useState(0);
  const [billDelay, setBillDelay] = useState(0);
  const [extraInflow, setExtraInflow] = useState(0);
  const [extraOutflow, setExtraOutflow] = useState(0);
  const scenarioActive =
    invoiceDelay !== 0 || billDelay !== 0 || extraInflow > 0 || extraOutflow > 0;

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      if (scenarioActive) {
        const today = new Date();
        const midDate = new Date(today.getTime() + Math.floor(days / 2) * 86400000)
          .toISOString()
          .slice(0, 10);
        const body = {
          days,
          invoice_delay_days: invoiceDelay,
          bill_delay_days: billDelay,
          extra_inflows:
            extraInflow > 0
              ? [{ date: midDate, amount: extraInflow, label: "Szenario" }]
              : [],
          extra_outflows:
            extraOutflow > 0
              ? [{ date: midDate, amount: extraOutflow, label: "Szenario" }]
              : [],
        };
        const r = await apiClient.post(
          `/companies/${companyId}/cashflow/forecast-scenario`,
          body
        );
        setData(r.data);
      } else {
        const r = await apiClient.get(
          `/companies/${companyId}/cashflow/forecast`,
          { params: { days } }
        );
        setData(r.data);
      }
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const resetScenario = () => {
    setInvoiceDelay(0);
    setBillDelay(0);
    setExtraInflow(0);
    setExtraOutflow(0);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [companyId, days, invoiceDelay, billDelay, extraInflow, extraOutflow]);

  const series =
    data?.series?.map((p) => ({
      date: p.date.slice(5),
      balance: p.balance,
      inflow: p.inflow,
      outflow: -p.outflow,
      payroll: -(p.payroll || 0),
      vat: -(p.vat || 0),
    })) || [];

  const minBalanceCritical = data && data.min_balance < 0;
  const breakdown = data?.breakdown || {};

  return (
    <Card
      className="p-4 border border-slate-200 bg-white"
      data-testid="cashflow-forecast-widget"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2">
          <TrendingUp className="h-4 w-4 text-sky-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900">
              Cashflow-Vorhersage
            </div>
            <div className="text-[11px] text-slate-500">
              Offene Rechnungen · Bills · Löhne · MWST
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Range Selector */}
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value)}
                data-testid={`cashflow-range-${r.value}`}
                className={`text-[11px] px-2 py-1 ${
                  days === r.value
                    ? "bg-sky-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {/* Iter100b: Was-wäre-wenn Toggle */}
          <Button
            variant={scenarioActive ? "default" : "ghost"}
            size="icon"
            className={`h-7 w-7 ${
              scenarioActive ? "bg-violet-600 hover:bg-violet-700 text-white" : ""
            }`}
            onClick={() => setScenarioOpen(!scenarioOpen)}
            data-testid="cashflow-scenario-toggle"
            title="Was-wäre-wenn-Szenario"
          >
            <Beaker className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={load}
            disabled={loading}
            data-testid="cashflow-refresh-btn"
            title="Aktualisieren"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {data && (
        <>
          {/* KPI Strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
            <div
              className="rounded bg-slate-50 p-2"
              data-testid="cashflow-kpi-start"
            >
              <div className="flex items-center gap-1 text-[10px] text-slate-500">
                <Wallet className="h-3 w-3" /> Startsaldo
              </div>
              <div className="font-mono text-sm" data-testid="cashflow-start">
                {fmt(data.start_balance)}
              </div>
            </div>
            <div
              className="rounded bg-emerald-50 p-2"
              data-testid="cashflow-kpi-inflow"
            >
              <div className="flex items-center gap-1 text-[10px] text-emerald-700">
                <TrendingUp className="h-3 w-3" /> Eingänge ({days}T)
              </div>
              <div className="font-mono text-sm">{fmt(data.total_inflow)}</div>
            </div>
            <div
              className="rounded bg-rose-50 p-2"
              data-testid="cashflow-kpi-outflow"
            >
              <div className="flex items-center gap-1 text-[10px] text-rose-700">
                <TrendingDown className="h-3 w-3" /> Ausgänge ({days}T)
              </div>
              <div className="font-mono text-sm">{fmt(data.total_outflow)}</div>
            </div>
            <div
              className={`rounded p-2 ${
                minBalanceCritical
                  ? "bg-rose-100 border border-rose-300"
                  : "bg-sky-50"
              }`}
              data-testid="cashflow-kpi-min"
            >
              <div className="flex items-center gap-1 text-[10px] text-slate-700">
                {minBalanceCritical ? (
                  <AlertTriangle className="h-3 w-3 text-rose-700" />
                ) : (
                  <Wallet className="h-3 w-3" />
                )}
                Tiefpunkt
              </div>
              <div
                className={`font-mono text-sm ${
                  minBalanceCritical
                    ? "text-rose-800 font-semibold"
                    : "text-slate-900"
                }`}
                data-testid="cashflow-min"
              >
                {fmt(data.min_balance)}
                {data.min_balance_date && (
                  <span className="text-[10px] text-slate-500 ml-1 font-normal">
                    am {data.min_balance_date.slice(5)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Endsaldo prominent */}
          <div
            className={`rounded p-2 mb-3 flex items-baseline justify-between ${
              data.end_balance >= 0 ? "bg-sky-50" : "bg-rose-50"
            }`}
            data-testid="cashflow-endsaldo-card"
          >
            <div>
              <div className="text-[10px] text-slate-700">
                Prognose Endsaldo nach {days} Tagen
              </div>
              <div
                className="font-mono text-base font-semibold"
                data-testid="cashflow-end"
              >
                CHF {fmt(data.end_balance)}
              </div>
            </div>
            {breakdown && (
              <div className="hidden sm:flex gap-3 text-[10px] text-slate-600">
                {breakdown.payroll_outflow > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Users className="h-3 w-3" /> Lohn:{" "}
                    <span className="font-mono">
                      {fmt(breakdown.payroll_outflow)}
                    </span>
                  </span>
                )}
                {breakdown.vat_outflow > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Landmark className="h-3 w-3" /> MWST:{" "}
                    <span className="font-mono">
                      {fmt(breakdown.vat_outflow)}
                    </span>
                  </span>
                )}
                {breakdown.bills_outflow > 0 && (
                  <span className="flex items-center gap-0.5">
                    <ReceiptIcon className="h-3 w-3" /> Bills:{" "}
                    <span className="font-mono">
                      {fmt(breakdown.bills_outflow)}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Iter100b: Was-wäre-wenn Szenario-Panel */}
          {scenarioOpen && (
            <div
              className="mb-3 rounded-lg border border-violet-200 bg-violet-50/50 p-3"
              data-testid="cashflow-scenario-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Beaker className="h-3.5 w-3.5 text-violet-700" />
                  <span className="text-xs font-medium text-violet-900">
                    Was-wäre-wenn Szenario
                  </span>
                  {scenarioActive && (
                    <span className="text-[10px] bg-violet-200 text-violet-900 px-1.5 py-0.5 rounded-full">
                      aktiv
                    </span>
                  )}
                </div>
                {scenarioActive && (
                  <button
                    type="button"
                    className="text-[11px] text-violet-700 hover:text-violet-900 flex items-center gap-0.5"
                    onClick={resetScenario}
                    data-testid="cashflow-scenario-reset"
                  >
                    <RotateCcw className="h-3 w-3" /> Zurücksetzen
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-600 flex items-center justify-between mb-1">
                    <span>Kundenrechnungen verzögern</span>
                    <span className="font-mono text-violet-800">
                      +{invoiceDelay}T
                    </span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={60}
                    step={5}
                    value={invoiceDelay}
                    onChange={(e) => setInvoiceDelay(Number(e.target.value))}
                    className="w-full accent-violet-600"
                    data-testid="cashflow-scenario-invoice-delay"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-600 flex items-center justify-between mb-1">
                    <span>Lieferanten-Zahlungen strecken</span>
                    <span className="font-mono text-violet-800">
                      +{billDelay}T
                    </span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={60}
                    step={5}
                    value={billDelay}
                    onChange={(e) => setBillDelay(Number(e.target.value))}
                    className="w-full accent-violet-600"
                    data-testid="cashflow-scenario-bill-delay"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-600 mb-1 block">
                    Sondereinnahme (CHF)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={extraInflow || ""}
                    placeholder="z.B. Investorengeld"
                    onChange={(e) => setExtraInflow(Number(e.target.value) || 0)}
                    className="w-full text-xs border border-slate-200 rounded px-2 py-1 font-mono"
                    data-testid="cashflow-scenario-extra-inflow"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-600 mb-1 block">
                    Sonderausgabe (CHF)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={extraOutflow || ""}
                    placeholder="z.B. Steuernachzahlung"
                    onChange={(e) => setExtraOutflow(Number(e.target.value) || 0)}
                    className="w-full text-xs border border-slate-200 rounded px-2 py-1 font-mono"
                    data-testid="cashflow-scenario-extra-outflow"
                  />
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-2">
                Sondereinnahmen/-ausgaben werden in der Mitte des Zeitraums eingebucht.
              </div>
            </div>
          )}


          {/* Warnings */}
          {data.warnings?.length > 0 && (
            <div className="space-y-1 mb-3" data-testid="cashflow-warnings">
              {data.warnings.map((w, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                >
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* Chart */}
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200} initialDimension={{ width: 800, height: 200 }}>
              <ComposedChart
                data={series}
                margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  interval={Math.max(1, Math.floor(series.length / 8))}
                  tick={{ fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 10 }} width={60} />
                <Tooltip
                  formatter={(v, name) => [fmt(Math.abs(v)), name]}
                  labelFormatter={(l) => `Datum: ${l}`}
                  contentStyle={{ fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                <Bar
                  dataKey="inflow"
                  stackId="flow"
                  fill="#10b981"
                  name="Eingänge"
                />
                <Bar
                  dataKey="outflow"
                  stackId="flow"
                  fill="#f43f5e"
                  name="Ausgänge"
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="#0284c7"
                  strokeWidth={2}
                  dot={false}
                  name="Saldo"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}
